import { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

export default function FinancePayoutHistory() {

  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [filters, setFilters] = useState({
    from: "",
    to: "",
    salesman: "",
    dealer: "",
    manager: "",
    status: ""
  });

  const token = localStorage.getItem("token");

  // ==========================
  // LOAD DATA
  // ==========================
  const load = async () => {

    try {

      const res = await axios.get(
        "https://sims.canonpk.com/api/finance/payouts",
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setData(res.data);
      setFiltered(res.data);

    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // ==========================
  // FILTER
  // ==========================
  const applyFilters = () => {

    let temp = [...data];

    if (filters.status) {
      temp = temp.filter(
        p => p.status === filters.status
      );
    }

    if (filters.salesman) {
      temp = temp.filter(p =>
        p.salesman_name
          ?.toLowerCase()
          .includes(filters.salesman.toLowerCase())
      );
    }

    if (filters.dealer) {
      temp = temp.filter(p =>
        p.dealer_name
          ?.toLowerCase()
          .includes(filters.dealer.toLowerCase())
      );
    }

    if (filters.manager) {
      temp = temp.filter(p =>
        p.manager_name
          ?.toLowerCase()
          .includes(filters.manager.toLowerCase())
      );
    }

    if (filters.from && filters.to) {

      temp = temp.filter(p => {

        const d = new Date(p.created_at);

        return (
          d >= new Date(filters.from) &&
          d <= new Date(filters.to)
        );

      });
    }

    setFiltered(temp);
  };

  // ==========================
  // MARK PAID
  // ==========================
  const markPaid = async (id) => {

    try {

      await axios.post(
        `https://sims.canonpk.com/api/finance/payout-paid/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      load();

    } catch {
      alert("Error marking paid");
    }
  };

  // ==========================
  // EXPORT EXCEL
  // ==========================
  const exportExcel = () => {

    const ws = XLSX.utils.json_to_sheet(filtered);

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      "Payouts"
    );

    XLSX.writeFile(wb, "payouts.xlsx");
  };

  // ==========================
  // TOTALS
  // ==========================
  const totalPaid = filtered
    .filter(p => p.status === "paid")
    .reduce(
      (sum, p) =>
        sum + Number(p.total_amount || 0),
      0
    );

  const totalPending = filtered
    .filter(p => p.status === "pending")
    .reduce(
      (sum, p) =>
        sum + Number(p.total_amount || 0),
      0
    );

  const totalAll = filtered.reduce(
    (sum, p) =>
      sum + Number(p.total_amount || 0),
    0
  );

  // ==========================
  // HIDE NULL / 0 VALUES
  // ==========================
  const showValue = (value) => {

    return value &&
      value !== 0 &&
      value !== "0" &&
      value !== null &&
      value !== "null" &&
      value !== ""
      ? value
      : "-";
  };

  // ==========================
  // UI
  // ==========================
  return (

    <div style={{ padding: 20 }}>

      <h2 style={{ marginBottom: 15 }}>
        📋 Finance Payout History
      </h2>

      {/* ================= CARDS ================= */}

      <div style={styles.cards}>

        <div style={styles.card}>
          💰 Total
          <br />
          <b>Rs. {totalAll}</b>
        </div>

        <div
          style={{
            ...styles.card,
            background: "#27ae60"
          }}
        >
          ✅ Paid
          <br />
          <b>Rs. {totalPaid}</b>
        </div>

        <div
          style={{
            ...styles.card,
            background: "#f39c12"
          }}
        >
          ⏳ Pending
          <br />
          <b>Rs. {totalPending}</b>
        </div>

      </div>

      {/* ================= FILTERS ================= */}

      <div style={styles.filters}>

        <input
          type="date"
          onChange={e =>
            setFilters(f => ({
              ...f,
              from: e.target.value
            }))
          }
        />

        <input
          type="date"
          onChange={e =>
            setFilters(f => ({
              ...f,
              to: e.target.value
            }))
          }
        />

        <input
          placeholder="Salesman"
          onChange={e =>
            setFilters(f => ({
              ...f,
              salesman: e.target.value
            }))
          }
        />

        <input
          placeholder="Dealer"
          onChange={e =>
            setFilters(f => ({
              ...f,
              dealer: e.target.value
            }))
          }
        />

        <input
          placeholder="Manager"
          onChange={e =>
            setFilters(f => ({
              ...f,
              manager: e.target.value
            }))
          }
        />

        <select
          onChange={e =>
            setFilters(f => ({
              ...f,
              status: e.target.value
            }))
          }
        >
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
        </select>

        <button
          onClick={applyFilters}
          style={styles.btn}
        >
          Apply
        </button>

        <button
          onClick={exportExcel}
          style={styles.btnSecondary}
        >
          Export
        </button>

      </div>

      {/* ================= TABLE ================= */}

      <div style={{ overflowX: "auto" }}>

        <table style={styles.table}>

          <thead>

            <tr>
              <th>ID</th>
              <th>Salesman</th>
              <th>Dealer</th>
              <th>Total</th>
              <th>Payment Method</th>
              <th>Payment Details</th>
              <th>Status</th>
              <th>Paid At</th>
              <th>Action</th>
            </tr>

          </thead>

          <tbody>

            {filtered.map(p => (

              <tr key={p.id}>

                <td>{showValue(p.id)}</td>

                <td>{showValue(p.salesman_name)}</td>

                <td>{showValue(p.dealer_name)}</td>

                <td>
                  Rs. {showValue(p.total_amount)}
                </td>

                {/* ================= PAYMENT METHOD ================= */}

                <td>

                  {showValue(p.payment_method) !== "-" ? (

                    <span
                      style={{
                        padding: "5px 10px",
                        borderRadius: 20,
                        color: "#fff",
                        fontSize: 12,
                        fontWeight: "bold",
                        background:
                          p.payment_method === "jazzcash"
                            ? "#f39c12"
                            : p.payment_method === "easypaisa"
                            ? "#27ae60"
                            : p.payment_method === "bank"
                            ? "#2980b9"
                            : "#555"
                      }}
                    >
                      {p.payment_method.toUpperCase()}
                    </span>

                  ) : "-"}

                </td>

                {/* ================= PAYMENT DETAILS ================= */}

                <td>

                  {p.payment_method === "jazzcash"
                    ? showValue(p.jazzcash_number)

                    : p.payment_method === "easypaisa"
                    ? showValue(p.easypaisa_number)

                    : p.payment_method === "bank"
                    ? showValue(p.bank_name)

                    : "-"}

                </td>

                {/* ================= STATUS ================= */}

                <td>

                  {p.status === "paid" ? (

                    <span
                      style={{
                        color: "green",
                        fontWeight: "bold"
                      }}
                    >
                      ● Paid
                    </span>

                  ) : (

                    <span
                      style={{
                        color: "orange",
                        fontWeight: "bold"
                      }}
                    >
                      ● Pending
                    </span>

                  )}

                </td>

                {/* ================= PAID DATE ================= */}

                <td>

                  {p.paid_at
                    ? new Date(
                        p.paid_at
                      ).toLocaleDateString()
                    : "-"}

                </td>

                {/* ================= ACTION ================= */}

                <td>

                  {p.status !== "paid" && (

                    <button
                      onClick={() => markPaid(p.id)}
                      style={styles.payBtn}
                    >
                      Mark Paid
                    </button>

                  )}

                </td>

              </tr>

            ))}

          </tbody>

        </table>

      </div>

    </div>
  );
}

// ==========================
// STYLES
// ==========================

const styles = {

  cards: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 20
  },

  card: {
    flex: 1,
    minWidth: 150,
    padding: 15,
    background: "#2c5178",
    color: "#fff",
    borderRadius: 10,
    textAlign: "center"
  },

  filters: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 15
  },

  btn: {
    background: "#2c5178",
    color: "#fff",
    border: "none",
    padding: "8px 12px",
    borderRadius: 5,
    cursor: "pointer"
  },

  btnSecondary: {
    background: "#555",
    color: "#fff",
    border: "none",
    padding: "8px 12px",
    borderRadius: 5,
    cursor: "pointer"
  },

  table: {
    width: "100%",
    borderCollapse: "collapse"
  },

  payBtn: {
    background: "orange",
    border: "none",
    padding: "6px 10px",
    color: "#fff",
    borderRadius: 5,
    cursor: "pointer"
  }
};